import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import '../styles/Settings.css';

interface HouseholdMember {
    id: string;
    user_id: string;
    role: 'owner' | 'member';
    user_name: string;
    user_email: string;
    joined_at: string;
}

interface HouseholdInvite {
    id: string;
    email: string;
    status: string;
    created_at: string;
    expires_at: string;
    household_name?: string;
    invited_by_name?: string;
}

interface HouseholdData {
    household: {
        id: string;
        name: string;
        created_at: string;
    };
    role: 'owner' | 'member';
    members: HouseholdMember[];
    pending_invites: HouseholdInvite[];
}

interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    message?: string;
}

export default function Settings() {
    const { token, user } = useAuth();
    const [household, setHousehold] = useState<HouseholdData | null>(null);
    const [pendingInvites, setPendingInvites] = useState<HouseholdInvite[]>([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        fetchHouseholdData();
        fetchPendingInvites();
    }, [token]);

    async function fetchHouseholdData() {
        try {
            const res = await fetch('/api/households', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data: ApiResponse<HouseholdData> = await res.json();
            if (data.success) {
                setHousehold(data.data ?? null);
            }
        } catch (error) {
            console.error('Failed to fetch household:', error);
        } finally {
            setLoading(false);
        }
    }

    async function fetchPendingInvites() {
        try {
            const res = await fetch('/api/households/invites', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data: ApiResponse<HouseholdInvite[]> = await res.json();
            if (data.success) {
                setPendingInvites(data.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch invites:', error);
        }
    }

    async function handleInvite(e: React.FormEvent) {
        e.preventDefault();
        if (!inviteEmail.trim()) return;

        setInviteLoading(true);
        setMessage(null);

        try {
            const res = await fetch('/api/households/invite', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ email: inviteEmail }),
            });

            const data: ApiResponse = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: data.message || 'Invitation sent!' });
                setInviteEmail('');
                fetchHouseholdData();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to send invitation' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to send invitation' });
        } finally {
            setInviteLoading(false);
        }
    }

    async function handleAcceptInvite(inviteId: string) {
        try {
            const res = await fetch(`/api/households/invites/${inviteId}/accept`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });

            const data: ApiResponse = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: data.message || 'Invitation accepted!' });
                fetchPendingInvites();
                fetchHouseholdData();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to accept invitation' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to accept invitation' });
        }
    }

    async function handleDeclineInvite(inviteId: string) {
        try {
            const res = await fetch(`/api/households/invites/${inviteId}/decline`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });

            const data: ApiResponse = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Invitation declined' });
                fetchPendingInvites();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to decline invitation' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to decline invitation' });
        }
    }

    async function handleRemoveMember(memberId: string) {
        if (!confirm('Are you sure you want to remove this member?')) return;

        try {
            const res = await fetch(`/api/households/members/${memberId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });

            const data: ApiResponse = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Member removed' });
                fetchHouseholdData();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to remove member' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to remove member' });
        }
    }

    if (loading) {
        return (
            <div className="settings-page">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="settings-page">
            <h1>Settings</h1>

            {message && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Pending Invitations for Current User */}
            {pendingInvites.length > 0 && (
                <section className="settings-section invites-received">
                    <h2>🎉 You Have Pending Invitations!</h2>
                    <div className="invite-cards">
                        {pendingInvites.map((invite) => (
                            <div key={invite.id} className="invite-card">
                                <div className="invite-info">
                                    <strong>{invite.household_name}</strong>
                                    <span>Invited by {invite.invited_by_name}</span>
                                </div>
                                <div className="invite-actions">
                                    <button
                                        className="btn-accept"
                                        onClick={() => handleAcceptInvite(invite.id)}
                                    >
                                        Accept
                                    </button>
                                    <button
                                        className="btn-decline"
                                        onClick={() => handleDeclineInvite(invite.id)}
                                    >
                                        Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Household Section */}
            <section className="settings-section">
                <h2>🏠 Household</h2>
                {household ? (
                    <div className="household-info">
                        <div className="household-name">
                            <label>Household Name</label>
                            <span>{household.household.name}</span>
                        </div>
                        <div className="household-role">
                            <label>Your Role</label>
                            <span className={`role-badge ${household.role}`}>
                                {household.role === 'owner' ? '👑 Owner' : '👤 Member'}
                            </span>
                        </div>
                    </div>
                ) : (
                    <p>No household found.</p>
                )}
            </section>

            {/* Members Section */}
            {household && (
                <section className="settings-section">
                    <h2>👥 Household Members</h2>
                    <div className="members-list">
                        {household.members.map((member) => (
                            <div key={member.id} className="member-card">
                                <div className="member-info">
                                    <span className="member-name">
                                        {member.user_name}
                                        {member.user_id === user?.id && ' (You)'}
                                    </span>
                                    <span className="member-email">{member.user_email}</span>
                                </div>
                                <div className="member-meta">
                                    <span className={`role-badge ${member.role}`}>
                                        {member.role === 'owner' ? '👑' : '👤'}
                                    </span>
                                    {household.role === 'owner' &&
                                        member.user_id !== user?.id &&
                                        member.role !== 'owner' && (
                                            <button
                                                className="btn-remove"
                                                onClick={() => handleRemoveMember(member.id)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Invite Section (Owner Only) */}
            {household?.role === 'owner' && (
                <section className="settings-section">
                    <h2>✉️ Invite Family Member</h2>
                    <form onSubmit={handleInvite} className="invite-form">
                        <input
                            type="email"
                            placeholder="Enter email address"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                        />
                        <button type="submit" disabled={inviteLoading}>
                            {inviteLoading ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </form>

                    {/* Pending Sent Invitations */}
                    {household.pending_invites.length > 0 && (
                        <div className="pending-invites">
                            <h3>Pending Invitations</h3>
                            <ul>
                                {household.pending_invites.map((invite) => (
                                    <li key={invite.id}>
                                        <span>{invite.email}</span>
                                        <span className="expires">
                                            Expires: {new Date(invite.expires_at).toLocaleDateString()}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            {/* Account Section */}
            <section className="settings-section">
                <h2>👤 Account</h2>
                <div className="account-info">
                    <div className="info-row">
                        <label>Name</label>
                        <span>{user?.name}</span>
                    </div>
                    <div className="info-row">
                        <label>Email</label>
                        <span>{user?.email}</span>
                    </div>
                </div>
            </section>
        </div>
    );
}
